<script setup lang="ts">
import { UserRolesEnum } from '~/utils/enums/userRoles'
import type { PreferredLanguage } from '~/models'
import type { SelectableWorkspace } from '~/stores/admin'

const languageStore = useLanguageStore()
const { updateUserLanguage, fetchLanguages } = languageStore
const { languages, interfaceLanguage } = storeToRefs(languageStore)
const { toggleMenu } = useMenuStore()
const { isMenuCollapsed } = storeToRefs(useMenuStore())
const { showAdminInterface, userRoles, userContext }
  = storeToRefs(useAuthStore())
const adminStore = useAdminStore()
const { selectedWorkspace, allAdministeredWorkspaces } = storeToRefs(adminStore)
const { fetchAllAdministeredWorkspaces } = adminStore
const { fetchUserContext } = useProjectStore()
const route = useRoute()

const fetching = ref(false)
const posting = ref(false)
const showWorkspaceSelectionModal = ref(false)

const workspaceOptions = ref<SelectableWorkspace[]>([])

async function setLanguage(locale?: PreferredLanguage) {
  if (!locale) {
    return
  }
  interfaceLanguage.value = locale

  const languageId = locale?.id
  await updateUserLanguage(posting, languageId)
  window.location.reload()
}

function switchBetweenAdminAndAdvisor() {
  if (showAdminInterface.value) {
    navigateTo('/projects')
    showAdminInterface.value = false
  }
  else if (selectedWorkspace.value) {
    navigateTo('/admin')
    showAdminInterface.value = true
    if (allAdministeredWorkspaces.value.length === 0) {
      fetchAllAdministeredWorkspaces(fetching)
    }
  }
  else {
    showWorkspaceSelectionModal.value = true
  }
}

function handleWorkspaceSelect(workspace: SelectableWorkspace) {
  selectedWorkspace.value = workspace
  // Close the dropdown by removing focus from the dropdown element
  const dropdownElement = document.activeElement as HTMLElement
  if (dropdownElement) {
    dropdownElement.blur()
  }
  // reload the window
  window.location.reload()
}

onMounted(async () => {
  const shouldByPassFetching
    = route.query.reportAccessToken && route.path.includes('/report')
  if (shouldByPassFetching) {
    return
  }
  userContext.value = await fetchUserContext(fetching)
  fetchLanguages(fetching)
  showAdminInterface.value = route.path.includes('/admin')

  const canAccessAdmin = userContext.value?.roles.some(
    role =>
      role.name === UserRolesEnum.GLOBAL_ADMIN
      || role.name === UserRolesEnum.WORKSPACE_ADMIN,
  )
  if (!canAccessAdmin)
    return

  if (allAdministeredWorkspaces.value.length === 0) {
    workspaceOptions.value = await fetchAllAdministeredWorkspaces(fetching, true)
    if (!selectedWorkspace.value && showAdminInterface.value) {
      showWorkspaceSelectionModal.value = true
    }
  }
  else if (allAdministeredWorkspaces.value.length !== 0) {
    workspaceOptions.value = allAdministeredWorkspaces.value
  }
})
</script>

<template>
  <div
    class="font-secu-body-bold z-[100] flex cursor-auto items-center justify-between bg-primary-navyblue-850 pr-spacing-lg">
    <div class="flex h-full gap-spacing-sm">
      <button class="pl-spacing-sm sm:hidden" @click="toggleMenu">
        <SvgoHamburger class="size-6" fill="white" />
      </button>
      <div
        class="relative flex shrink-0 cursor-default items-center overflow-hidden bg-primary-navyblue-900 p-spacing-sm text-white transition-[width] duration-300 ease-in"
        :class="{
          'w-64': !isMenuCollapsed,
          'w-20': isMenuCollapsed,
        }">
        <div class="flex items-center gap-spacing-sm whitespace-nowrap">
          <SvgoAdvisor class="size-6 shrink-0" />
          <span
            class="text-body-xl leading-xl"
            :class="{ invisible: isMenuCollapsed }">Advisor</span>
        </div>
        <button
          class="absolute right-spacing-sm hidden items-center rounded-md bg-primary-navyblue-700 p-spacing-xs sm:flex"
          @mouseenter="isMenuCollapsed = false"
          @click="toggleMenu">
          <SvgoArrowRight v-if="isMenuCollapsed" class="size-6 fill-white" />
          <SvgoArrowLeft v-else class="size-6 fill-white" />
        </button>
      </div>
    </div>
    <div class="flex items-center gap-spacing-sm">
      <SButton
        v-if="
          userRoles.includes(UserRolesEnum.GLOBAL_ADMIN) || userRoles.includes(UserRolesEnum.WORKSPACE_ADMIN)
        "
        size="xs"
        :text="showAdminInterface ? $t('switch_advisor') : $t('switch_admin')"
        class="hidden h-9 border-2 border-primary-navyblue-100 text-primary-navyblue-100 sm:flex"
        @click="switchBetweenAdminAndAdvisor" />
      <div class="dropdown dropdown-end">
        <div
          v-if="interfaceLanguage?.name"
          class="secu-body-small-bold flex cursor-pointer items-center gap-spacing-sm px-spacing-md py-spacing-xs text-white"
          tabindex="0">
          <LanguageItem
            :language="interfaceLanguage?.name"
            :language-tag="interfaceLanguage?.tag" />
          <SvgoArrowDown class="size-4 fill-white" />
        </div>
        <ul
          tabindex="0"
          class="secu-body-small menu dropdown-content z-[1] max-h-[85vh] w-fit overflow-y-auto overflow-x-hidden rounded-lg border-2 border-accent-purple-400 bg-white p-spacing-md shadow">
          <div>
            <li
              v-for="locale in languages"
              :key="locale.tag"
              @click="setLanguage(locale)">
              <a class="whitespace-nowrap p-spacing-sm">
                <LanguageItem
                  v-if="locale.name"
                  :language="locale.name"
                  :language-tag="locale.tag" />
              </a>
            </li>
          </div>
        </ul>
      </div>
      <div
        v-if="showAdminInterface && selectedWorkspace"
        class="dropdown dropdown-end">
        <div
          v-if="selectedWorkspace"
          class="secu-body-small-bold flex cursor-pointer items-center gap-spacing-sm px-spacing-md py-spacing-xs text-white"
          tabindex="0">
          <span>{{ selectedWorkspace.name }}</span>
          <SvgoArrowDown class="size-4 fill-white" />
        </div>
        <ul
          tabindex="0"
          class="secu-body-small menu dropdown-content z-[1] max-h-[85vh] w-fit overflow-y-auto overflow-x-hidden rounded-lg border-2 border-accent-purple-400 bg-white p-spacing-md shadow">
          <div>
            <li
              v-for="workspace in workspaceOptions"
              :key="workspace.id"
              @click="handleWorkspaceSelect(workspace)">
              <a class="whitespace-nowrap p-spacing-sm">
                <span>{{ workspace.name }}</span>
              </a>
            </li>
          </div>
        </ul>
      </div>
    </div>
    <WorkspaceSelectionModal
      v-if="showWorkspaceSelectionModal"
      v-model="showWorkspaceSelectionModal" />
  </div>
</template>
